import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import {
  scacInfoService,
  type ScacLocationInfo,
  type ContactBlock,
  type RpCyLocation,
} from '../../services/scacInfo.service';
import Tabs, { type TabDef } from '../../pages/pro/components/Tabs';

export interface ScacInfoModalProps {
  isOpen: boolean;
  scac: string;
  movetype: string;
  pronumber?: number;
  /** The pro's revenue region — used to default the active terminal tab. */
  region?: string;
  onClose: () => void;
}

const isImportLike = (mt: string) => {
  const m = (mt || '').toUpperCase();
  return m === 'IMPORT' || m === 'DOMESTIC';
};
const isExport = (mt: string) => (mt || '').toUpperCase() === 'EXPORT';
const showPickupReturn = (mt: string) => isImportLike(mt) || isExport(mt);

/** Small left-label / right-value row used throughout the body. */
function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="w-44 shrink-0 text-right font-semibold text-slate-600 dark:text-gray-400">
        {label}
      </span>
      <span className="flex-1 min-w-0 text-slate-900 dark:text-gray-100 break-words">
        {children}
      </span>
    </div>
  );
}

function ExternalLink({ href }: { href: string }) {
  const url = /^https?:\/\//i.test(href) ? href : `http://${href}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 break-all"
    >
      {href}
      <ArrowTopRightOnSquareIcon className="w-3 h-3 shrink-0" />
    </a>
  );
}

function PickupReturnTable({
  rows,
  movetype,
}: {
  rows: RpCyLocation[];
  movetype: string;
}) {
  // IMPORT/DOMESTIC: Pickup = rail/port, Return = cy. EXPORT: reversed.
  // (cms-dev/lib/main.pl:2219-2264)
  const swap = isExport(movetype);
  const pickup = (r: RpCyLocation) => (swap ? r.cy : r.railPort);
  const ret = (r: RpCyLocation) => (swap ? r.railPort : r.cy);

  return (
    <table className="w-full text-[0.75rem] mt-1">
      <thead>
        <tr className="text-left text-slate-500 dark:text-gray-400">
          <th className="font-semibold py-1 pr-2 underline">Pickup</th>
          <th className="font-semibold py-1 pr-2 underline">Return</th>
          <th className="font-semibold py-1 underline">Other</th>
        </tr>
      </thead>
      <tbody className="align-top text-slate-900 dark:text-gray-100">
        {rows.length === 0 ? (
          <tr>
            <td className="py-0.5 pr-2 italic text-slate-400 dark:text-gray-500">n/a</td>
            <td className="py-0.5 pr-2 italic text-slate-400 dark:text-gray-500">n/a</td>
            <td className="py-0.5 italic text-slate-400 dark:text-gray-500">n/a</td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i}>
              <td className="py-0.5 pr-2">{pickup(r) || '—'}</td>
              <td className="py-0.5 pr-2">{ret(r) || '—'}</td>
              <td className="py-0.5">{r.other || '—'}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function ContactCard({ block }: { block: ContactBlock }) {
  const primary = block.phone || block.email;
  const hasBoth = block.email && block.phone;
  const label =
    block.railbillFlag != null
      ? `${block.label} [ ${block.railbillFlag || 'N'} ]`
      : block.label;

  // Skip a block that has no information at all.
  const empty =
    !block.phone &&
    !block.email &&
    !block.contact &&
    !block.fax &&
    !block.website &&
    !block.other &&
    !block.instructions;
  if (empty) return null;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-50/60 dark:bg-gray-900/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-700 dark:text-gray-200">{label}</span>
        {primary && (
          <span className="text-slate-900 dark:text-gray-100 text-right break-all">
            {block.phone ? (
              <a
                href={`tel:${block.phone}`}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
              >
                {block.phone}
              </a>
            ) : (
              <a
                href={`mailto:${block.email}`}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
              >
                {block.email}
              </a>
            )}
          </span>
        )}
      </div>
      <div className="mt-0.5 space-y-0.5 text-[0.71875rem] text-slate-600 dark:text-gray-400">
        {block.contact && (
          <div>
            <span className="font-medium">Contact:</span> {block.contact}
          </div>
        )}
        {block.fax && (
          <div>
            <span className="font-medium">Fax:</span> {block.fax}
          </div>
        )}
        {hasBoth && (
          <div>
            <span className="font-medium">Email:</span>{' '}
            <a
              href={`mailto:${block.email}`}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 break-all"
            >
              {block.email}
            </a>
          </div>
        )}
        {block.website && (
          <div>
            <span className="font-medium">Website:</span> <ExternalLink href={block.website} />
          </div>
        )}
        {block.instructions && (
          <div>
            <div className="font-medium">Instructions:</div>
            <div className="mt-1 whitespace-pre-wrap break-words leading-relaxed">{block.instructions}</div>
          </div>
        )}
        {block.other && (
          <div>
            <div className="font-medium">Other:</div>
            <div className="mt-1 whitespace-pre-wrap break-words leading-relaxed">{block.other}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function LocationBody({ loc, movetype }: { loc: ScacLocationInfo; movetype: string }) {
  return (
    <div className="space-y-3 text-[0.78125rem]">
      {/* Top section — tightened and left-anchored, capped near the tab-bar
          width on large screens rather than spreading across the full modal. */}
      <div className="max-w-3xl space-y-2">
        <div>
          <KV label="Name:">{loc.scacName || '—'}</KV>
          <KV label="SCAC:">{loc.scac || '—'}</KV>
        </div>

        {showPickupReturn(movetype) && (
          <PickupReturnTable rows={loc.rpCyLocations} movetype={movetype} />
        )}

        <div>
          <KV label="Preferred Chassis Provider:">{loc.preferredChassisProvider || '—'}</KV>
          <KV label="Pool:">{loc.chassisPool || '—'}</KV>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {loc.contacts.map((b) => (
          <ContactCard key={b.key} block={b} />
        ))}
      </div>

      {loc.website && (
        <KV label="SSLine Website:">
          <ExternalLink href={loc.website} />
        </KV>
      )}

      {loc.notes && (
        <KV label="Notes:">
          <span className="whitespace-pre-line">{loc.notes}</span>
        </KV>
      )}
    </div>
  );
}

const ScacInfoModal: React.FC<ScacInfoModalProps> = ({
  isOpen,
  scac,
  movetype,
  pronumber,
  region,
  onClose,
}) => {
  const trimmedScac = (scac || '').trim();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['scac-info', trimmedScac, movetype, pronumber],
    queryFn: () => scacInfoService.get(trimmedScac, { movetype, pronumber }),
    enabled: isOpen && !!trimmedScac,
    staleTime: 5 * 60 * 1000,
  });

  const [activeId, setActiveId] = useState<string>('');
  // Set the default tab once per load; don't override the user's tab clicks on refetch.
  const defaultedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const key = `${trimmedScac}|${movetype}|${pronumber ?? ''}|${region ?? ''}`;
    if (defaultedFor.current === key) return;
    const names = data.locations.map((l) => l.location);
    const norm = (s: string | null | undefined) => (s ?? '').trim().toUpperCase();
    // Prefer the server-resolved region (handles BROKERAGE/MANAGEMENT via
    // ratezones), then the pro's revenue region; match terminal tabs
    // case-insensitively. Fall back to the first terminal.
    let next = '';
    for (const candidate of [data.defaultLocation, region]) {
      if (!candidate) continue;
      const hit = names.find((n) => norm(n) === norm(candidate));
      if (hit) {
        next = hit;
        break;
      }
    }
    if (!next) next = names[0] ?? '';
    setActiveId(next);
    defaultedFor.current = key;
  }, [data, trimmedScac, movetype, pronumber, region]);

  // Reset the default-tab guard when the modal closes so reopening re-defaults.
  useEffect(() => {
    if (!isOpen) defaultedFor.current = null;
  }, [isOpen]);

  // Escape to close.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const activeLoc = useMemo(
    () => data?.locations.find((l) => l.location === activeId),
    [data, activeId],
  );

  const tabs = useMemo<TabDef[]>(
    () => (data?.locations ?? []).map((l) => ({ id: l.location, label: l.location })),
    [data],
  );

  // Read-only SCAC lookup: Alt+C closes; the layer keeps Alt+S off the Pro
  // drawer's Save behind it.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const headerScacName = activeLoc?.scacName || data?.locations[0]?.scacName || '';
  const titleSuffix = headerScacName
    ? `${headerScacName} (${trimmedScac})`
    : trimmedScac;

  return createPortal(
    <div
      className="fixed inset-0 bg-scrim/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden w-[75vw] max-w-none max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="SCAC Contact Info"
      >
        {/* Header */}
        <ModalHeader
          className="rounded-t-2xl"
          title={`SCAC Contact Info — ${titleSuffix}`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        {/* Terminal tabs — shared Pro 360 tab bar */}
        {tabs.length > 0 && (
          <Tabs tabs={tabs} activeId={activeId} onChange={setActiveId} />
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500 dark:text-gray-400">
              <span className="inline-block w-4 h-4 mr-2 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Loading…
            </div>
          )}
          {!isLoading && isError && (
            <div className="py-12 text-center text-sm text-red-600 dark:text-red-400">
              Could not load SCAC contact info. Please try again.
            </div>
          )}
          {!isLoading && !isError && data && data.locations.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-500 dark:text-gray-400">
              No contact information on file for this SCAC.
            </div>
          )}
          {!isLoading && !isError && activeLoc && (
            <LocationBody loc={activeLoc} movetype={data?.movetype ?? movetype} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ScacInfoModal;
